import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Space, Alert, Modal, Form, Input, InputNumber,
  Switch, Popconfirm, Tabs, Typography, message
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { adminAPI } from '../api/admin';

const { Text } = Typography;

function UnreachableAlert({ show }) {
  if (!show) return null;
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
      message="اتصال به کوچه دیاگون برقرار نیست"
      description="اپ کوچه دیاگون (da.potterhead.ir) پاسخ نداد. اگر آن سرویس پایین است یا JWT_SECRET با بک‌اند اصلی یکسان نیست را بررسی کنید."
    />
  );
}

function VaultsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reachable, setReachable] = useState(true);

  const fetchList = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDiagonAlleyVaults({ page: p, limit: 20 });
      setItems(data.vaults || []);
      setTotal(data.total || 0);
      setReachable(data.diagonAlleyReachable !== false);
    } catch (e) {
      message.error('خطا در دریافت خزانه‌ها');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => { fetchList(page); }, [page, fetchList]);

  const columns = [
    { title: 'کاربر', dataIndex: 'userId', width: 220 },
    { title: 'گالیون', dataIndex: 'galleons', width: 100 },
    { title: 'سیکل', dataIndex: 'sickles', width: 100 },
    { title: 'نات', dataIndex: 'knuts', width: 100 },
    { title: 'ایجاد', dataIndex: 'createdAt', render: (v) => v ? moment(v).format('YYYY-MM-DD HH:mm') : '—' }
  ];

  return (
    <div>
      <UnreachableAlert show={!reachable} />
      <Card size="small" extra={<Button icon={<ReloadOutlined />} onClick={() => fetchList(page)}>بروزرسانی</Button>}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, onChange: setPage }}
        />
      </Card>
    </div>
  );
}

function OrdersTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reachable, setReachable] = useState(true);

  const fetchList = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDiagonAlleyOrders({ page: p, limit: 20 });
      setItems(data.orders || []);
      setTotal(data.total || 0);
      setReachable(data.diagonAlleyReachable !== false);
    } catch (e) {
      message.error('خطا در دریافت سفارش‌ها');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => { fetchList(page); }, [page, fetchList]);

  const columns = [
    { title: 'کاربر', dataIndex: 'userId', width: 220 },
    { title: 'جمع (گالیون)', dataIndex: 'totalGalleons', width: 130 },
    { title: 'وضعیت', dataIndex: 'status', width: 120, render: (s) => <Tag>{s}</Tag> },
    { title: 'ایجاد', dataIndex: 'createdAt', render: (v) => v ? moment(v).format('YYYY-MM-DD HH:mm') : '—' }
  ];

  return (
    <div>
      <UnreachableAlert show={!reachable} />
      <Card size="small" extra={<Button icon={<ReloadOutlined />} onClick={() => fetchList(page)}>بروزرسانی</Button>}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, onChange: setPage }}
        />
      </Card>
    </div>
  );
}

function DiscountsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reachable, setReachable] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDiagonAlleyDiscounts();
      setItems(data.discounts || []);
      setReachable(data.diagonAlleyReachable !== false);
    } catch (e) {
      message.error('خطا در دریافت کدهای تخفیف');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await adminAPI.updateDiagonAlleyDiscount(editing._id, values);
        message.success('بروزرسانی شد');
      } else {
        await adminAPI.createDiagonAlleyDiscount(values);
        message.success('ایجاد شد');
      }
      setModalOpen(false);
      fetchList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'عملیات ناموفق بود');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteDiagonAlleyDiscount(id);
      message.success('حذف شد');
      fetchList();
    } catch (e) {
      message.error('حذف ناموفق بود');
    }
  };

  const columns = [
    { title: 'کد', dataIndex: 'code', width: 140 },
    { title: 'نوع', dataIndex: 'type', width: 140 },
    { title: 'مقدار', dataIndex: 'value', width: 100 },
    { title: 'استفاده‌شده', dataIndex: 'usedCount', width: 110 },
    { title: 'وضعیت', dataIndex: 'isActive', width: 100, render: (v) => v ? <Tag color="success">فعال</Tag> : <Tag>غیرفعال</Tag> },
    {
      title: 'عملیات', width: 140, render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="حذف؟" onConfirm={() => handleDelete(r._id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <UnreachableAlert show={!reachable} />
      <Card
        size="small"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchList}>بروزرسانی</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>کد تخفیف جدید</Button>
          </Space>
        }
      >
        <Table rowKey="_id" columns={columns} dataSource={items} loading={loading} pagination={false} />
      </Card>

      <Modal title={editing ? 'ویرایش کد تخفیف' : 'کد تخفیف جدید'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSubmit} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="کد" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="توضیحات"><Input /></Form.Item>
          <Form.Item name="value" label="مقدار (درصد یا گالیون ثابت)" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="maxUses" label="حداکثر تعداد استفاده (۰ = نامحدود)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="isActive" label="فعال" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function AchievementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reachable, setReachable] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDiagonAlleyAchievements();
      setItems(data.achievements || []);
      setReachable(data.diagonAlleyReachable !== false);
    } catch (e) {
      message.error('خطا در دریافت دستاوردها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await adminAPI.updateDiagonAlleyAchievement(editing._id, values);
        message.success('بروزرسانی شد');
      } else {
        await adminAPI.createDiagonAlleyAchievement(values);
        message.success('ایجاد شد');
      }
      setModalOpen(false);
      fetchList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'عملیات ناموفق بود');
    }
  };

  const columns = [
    { title: 'اسلاگ', dataIndex: 'slug', width: 160 },
    { title: 'عنوان فارسی', dataIndex: 'titleFa' },
    { title: 'XP', dataIndex: 'xp', width: 80 },
    { title: 'فعال', dataIndex: 'isActive', width: 90, render: (v) => v ? <Tag color="success">بله</Tag> : <Tag>خیر</Tag> },
    { title: 'مخفی', dataIndex: 'isHidden', width: 90, render: (v) => v ? <Tag color="purple">بله</Tag> : <Tag>خیر</Tag> },
    { title: 'عملیات', width: 90, render: (_, r) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /> }
  ];

  return (
    <div>
      <UnreachableAlert show={!reachable} />
      <Card
        size="small"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchList}>بروزرسانی</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>دستاورد جدید</Button>
          </Space>
        }
      >
        <Table rowKey="_id" columns={columns} dataSource={items} loading={loading} pagination={false} />
      </Card>

      <Modal title={editing ? 'ویرایش دستاورد' : 'دستاورد جدید'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSubmit} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="slug" label="اسلاگ" rules={[{ required: true }]}><Input disabled={!!editing} /></Form.Item>
          <Form.Item name="title" label="عنوان (انگلیسی)"><Input /></Form.Item>
          <Form.Item name="titleFa" label="عنوان فارسی"><Input /></Form.Item>
          <Form.Item name="descriptionFa" label="توضیحات فارسی"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="xp" label="XP"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="isActive" label="فعال" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item name="isHidden" label="مخفی (سورپرایز)" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function MissionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reachable, setReachable] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDiagonAlleyMissions();
      setItems(data.missions || []);
      setReachable(data.diagonAlleyReachable !== false);
    } catch (e) {
      message.error('خطا در دریافت ماموریت‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await adminAPI.updateDiagonAlleyMission(editing._id, values);
        message.success('بروزرسانی شد');
      } else {
        await adminAPI.createDiagonAlleyMission(values);
        message.success('ایجاد شد');
      }
      setModalOpen(false);
      fetchList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'عملیات ناموفق بود');
    }
  };

  const columns = [
    { title: 'عنوان', dataIndex: 'title' },
    { title: 'وضعیت', dataIndex: 'isActive', width: 100, render: (v) => v ? <Tag color="success">فعال</Tag> : <Tag>غیرفعال</Tag> },
    { title: 'عملیات', width: 90, render: (_, r) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /> }
  ];

  return (
    <div>
      <UnreachableAlert show={!reachable} />
      <Card
        size="small"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchList}>بروزرسانی</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>ماموریت جدید</Button>
          </Space>
        }
      >
        <Table rowKey="_id" columns={columns} dataSource={items} loading={loading} pagination={false} />
      </Card>

      <Modal title={editing ? 'ویرایش ماموریت' : 'ماموریت جدید'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSubmit} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="عنوان" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="توضیحات"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="isActive" label="فعال" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function ConfigTab() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [reachable, setReachable] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDiagonAlleyConfig();
      setConfig(data.config || {});
      setReachable(data.diagonAlleyReachable !== false);
    } catch (e) {
      message.error('خطا در دریافت تنظیمات کوچه دیاگون');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const rows = Object.entries(config || {}).map(([key, value]) => ({ key, value: typeof value === 'object' ? JSON.stringify(value) : String(value) }));

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await adminAPI.setDiagonAlleyConfig(values.key, values.value);
      message.success('ذخیره شد');
      setModalOpen(false);
      fetchConfig();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'ذخیره ناموفق بود');
    }
  };

  const columns = [
    { title: 'کلید', dataIndex: 'key', width: 220 },
    { title: 'مقدار', dataIndex: 'value' }
  ];

  return (
    <div>
      <UnreachableAlert show={!reachable} />
      <Card
        size="small"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchConfig}>بروزرسانی</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true); }}>تنظیم جدید</Button>
          </Space>
        }
      >
        <Table rowKey="key" columns={columns} dataSource={rows} loading={loading} pagination={false} />
      </Card>

      <Modal title="ذخیره تنظیم" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSubmit} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="key" label="کلید" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="value" label="مقدار (متن یا JSON)" rules={[{ required: true }]}><Input.TextArea rows={3} /></Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>اگر مقدار JSON معتبر باشد به‌صورت آبجکت ذخیره می‌شود، در غیر این صورت متن ساده.</Text>
      </Modal>
    </div>
  );
}

export default function DiagonAlleyManager() {
  return (
    <Tabs
      items={[
        { key: 'vaults', label: 'خزانه‌ها', children: <VaultsTab /> },
        { key: 'orders', label: 'سفارش‌ها', children: <OrdersTab /> },
        { key: 'discounts', label: 'کدهای تخفیف', children: <DiscountsTab /> },
        { key: 'achievements', label: 'دستاوردها', children: <AchievementsTab /> },
        { key: 'missions', label: 'ماموریت‌ها', children: <MissionsTab /> },
        { key: 'config', label: 'تنظیمات', children: <ConfigTab /> }
      ]}
    />
  );
}
